import type { Selector } from '#types/selector.type';

import { isDefined } from '#misc/is-defined.guard';
import { isNotEmpty } from '#misc/is-not-empty';

/**
 * Removes all non-nullable values from the given array.
 * @template [TData=unknown]
 * @param {TData[]} items The array of items to be filtered.
 * @returns {NonNullable<TData>[]} An array containing the only the defined values.
 */
export function filterNotDefined<TData = unknown>(items: TData[]): NonNullable<TData>[];
/**
 * Removes all nullable types from the given array.
 * @template [TData]
 * @param {TData[]} items The array of items to be filtered.
 * @param {Selector} selector The selector used to determine which property of the object will be used.
 * @returns {NonNullable<TData<TKey>>[]} An array containing the only the defined values.
 */
export function filterNotDefined<TData extends Record<PropertyKey, unknown>, TKey extends keyof TData>(
  items: TData[],
  selector: Selector<TData, TKey>
): NonNullable<TData[TKey]>[];
/**
 * Removes all non-nullable types from the given array.
 * @template [TData]
 * @param {TData[]} items The array of items to be filtered.
 * @param {Selector} selector The selector used to determine which property of the object will be used.
 * @returns {(NonNullable<TData> | NonNullable<TData<TKey>>)[]} An array containing the only the defined values.
 */
export function filterNotDefined<TData, TKey extends keyof TData>(items: TData[], selector?: Selector<TData, TKey>) {
  const filtered = items.flatMap((item) => {
    const value = isDefined(selector) ? selector(item) : item;

    return isDefined(value) && isNotEmpty(value) ? [value] : [];
  });

  return filtered;
}
